import React, { useState, useEffect, useRef } from "react";
import styles from "../css/comuns/FAQ.module.css";
import {
    FaSearch, FaChevronDown, FaChevronUp, FaWifi, FaPlay,
    FaMobileAlt, FaUserEdit, FaExchangeAlt, FaUserFriends,
    FaShieldAlt, FaTabletAlt, FaHeadset, FaInfoCircle
} from "react-icons/fa";
import { config } from '../../data';

function FAQ() {
    // Estados para controle da interface
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("internet");
    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const searchInputRef = useRef(null);

    // Categorias com ícones
    const categories = [
        { id: "internet", name: config.texts.faq.categories.internet, icon: <FaWifi /> },
        { id: "oniplay", name: config.texts.faq.categories.oniplay, icon: <FaPlay /> },
        { id: "oni5g", name: config.texts.faq.categories.oni5g, icon: <FaMobileAlt /> },
        { id: "dados", name: config.texts.faq.categories.dados, icon: <FaUserEdit /> },
        { id: "planos", name: config.texts.faq.categories.planos, icon: <FaExchangeAlt /> },
        { id: "indicacao", name: config.texts.faq.categories.indicacao, icon: <FaUserFriends /> },
        { id: "seguranca", name: config.texts.faq.categories.seguranca, icon: <FaShieldAlt /> },
        { id: "aplicativos", name: config.texts.faq.categories.aplicativos, icon: <FaTabletAlt /> },
        { id: "suporte", name: config.texts.faq.categories.suporte, icon: <FaHeadset /> },
    ];

    // Base de dados de perguntas e respostas do config
    const faqData = config.content.faq;

    // Função para alternar a expansão de uma pergunta
    const toggleQuestion = (questionId) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };

    // Efeito para pesquisa
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        setIsSearching(true);

        // Simula um pequeno delay para dar a sensação de busca
        const timer = setTimeout(() => {
            const results = [];

            // Busca em todas as categorias
            Object.entries(faqData).forEach(([categoryId, questions]) => {
                const categoryName = categories.find(c => c.id === categoryId)?.name || categoryId;

                const matchingQuestions = questions.filter(q =>
                    q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    q.answer.toLowerCase().includes(searchTerm.toLowerCase())
                );

                matchingQuestions.forEach(q => {
                    results.push({
                        categoryId,
                        categoryName,
                        ...q
                    });
                });
            });

            setSearchResults(results);
            setShowSearchResults(true);
            setIsSearching(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Função para focar na barra de pesquisa
    const focusSearchInput = () => {
        searchInputRef.current?.focus();
    };

    // Função para limpar a pesquisa
    const clearSearch = () => {
        setSearchTerm("");
        setShowSearchResults(false);
    };

    // Função para selecionar uma categoria a partir dos resultados de pesquisa
    const selectFromSearch = (categoryId, questionId) => {
        setActiveCategory(categoryId);
        setSearchTerm("");
        setShowSearchResults(false);

        // Expande a pergunta selecionada
        setTimeout(() => {
            setExpandedQuestions(prev => ({
                ...prev,
                [questionId]: true
            }));

            // Rola até a pergunta
            const element = document.getElementById(questionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    return (
        <div className={styles.container}>
            {/* Cabeçalho com logo e título */}
            <div className={styles.header}>
                <div className={styles.logo}>
                    <span className={styles.logoText}>Oni</span>
                    <span className={styles.logoAccent}>telecom</span>
                </div>
                <h1 className={styles.title}>Central de Ajuda</h1>
            </div>

            <div className="container">
                {/* Barra de pesquisa com animação */}
                <div className={styles.searchContainer}>
                    <div className={styles.searchWrapper}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className={styles.searchInput}
                            placeholder="Pesquisar perguntas frequentes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className={styles.clearButton} onClick={clearSearch}>
                                ×
                            </button>
                        )}
                    </div>

                    {/* Resultados da pesquisa */}
                    {showSearchResults && (
                        <div className={styles.searchResults}>
                            <div className={styles.searchResultsHeader}>
                                {isSearching ? (
                                    <div className={styles.searchingIndicator}>Pesquisando...</div>
                                ) : (
                                    <div className={styles.resultsCount}>
                                        {searchResults.length} resultados encontrados
                                    </div>
                                )}
                            </div>

                            {searchResults.length > 0 ? (
                                <ul className={styles.resultsList}>
                                    {searchResults.map((result) => (
                                        <li
                                            key={result.id}
                                            className={styles.resultItem}
                                            onClick={() => selectFromSearch(result.categoryId, result.id)}
                                        >
                                            <div className={styles.resultCategory}>
                                                {result.categoryName}
                                            </div>
                                            <div className={styles.resultQuestion}>
                                                {result.question}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : !isSearching && (
                                <div className={styles.noResults}>
                                    <FaInfoCircle className={styles.noResultsIcon} />
                                    <p>Nenhum resultado encontrado para "{searchTerm}"</p>
                                    <p>Tente usar termos diferentes ou navegue pelas categorias</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="row">
                    {/* Menu de categorias */}
                    <div className="col-lg-3 col-md-4">
                        <div className={styles.categoriesContainer}>
                            <h3 className={styles.categoriesTitle}>Principais temas</h3>
                            <ul className={styles.categoriesList}>
                                {categories.map((category) => (
                                    <li
                                        key={category.id}
                                        className={`${styles.categoryItem} ${activeCategory === category.id ? styles.active : ""
                                            }`}
                                        onClick={() => setActiveCategory(category.id)}
                                    >
                                        <span className={styles.categoryIcon}>{category.icon}</span>
                                        <span className={styles.categoryName}>{category.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Conteúdo principal - Perguntas e respostas */}
                    <div className="col-lg-9 col-md-8">
                        <div className={styles.faqContainer}>
                            <h2 className={styles.faqTitle}>
                                {categories.find(c => c.id === activeCategory)?.name || "Perguntas Frequentes"}
                            </h2>

                            <div className={styles.faqList}>
                                {faqData[activeCategory]?.map((item) => (
                                    <div
                                        id={item.id}
                                        key={item.id}
                                        className={`${styles.faqItem} ${expandedQuestions[item.id] ? styles.expanded : ""}`}
                                    >
                                        <div
                                            className={styles.faqQuestion}
                                            onClick={() => toggleQuestion(item.id)}
                                        >
                                            <span>{item.question}</span>
                                            <span className={styles.faqToggle}>
                                                {expandedQuestions[item.id] ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </div>

                                        <div className={styles.faqAnswer}>
                                            <p>{item.answer}</p>
                                        </div>
                                    </div>
                                ))}

                                {(!faqData[activeCategory] || faqData[activeCategory].length === 0) && (
                                    <div className={styles.emptyState}>
                                        <p>Não há perguntas frequentes para esta categoria.</p>
                                        <button
                                            className={styles.searchButton}
                                            onClick={focusSearchInput}
                                        >
                                            Pesquisar em todas as categorias
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rodapé */}
            <div className={styles.footer}>
                <p>Não encontrou o que procurava? <a href="/contato" className={styles.footerLink}>Entre em contato com nosso suporte</a></p>
            </div>
        </div>
    );
}

export default FAQ;